import { createRouter, createWebHistory, type LocationQueryValue } from 'vue-router'
import { authRouteGuard, reloginRouteGuard, betaRouteGuard, premiumRouteGuard } from "@/router/auth_guards.ts";
import { internalOrSchoolAdminRouteGuard } from "@/router/internal-or-school-admin-route-guard.ts";
import { setTitle } from '@/util/title'
import HomeView from '@/views/HomeView.vue'
import FolderList from '@/library/FolderList.vue'
import * as constants from '@/library/library_constants'
import { CONCLUSION_ROUTE_NAMES } from '@/presentlevels/no-ai/conclusion_route_names'
const ComponentLib = () => import('@/componentlibrary/ComponentLib.vue')
const HomePage = () => import('@/home/HomePage.vue')
const GoalIndexPage = () => import('@/goalindex/GoalIndexPage.vue')
const StrategyIndexPage = () => import('@/strategies/udlindex/StrategyIndexPage.vue')
const BrowseStrategiesPage = () => import('@/strategies/BrowseStrategiesPage.vue')
const BrowseAssessmentsPage = () => import('@/browse/assessments/BrowseAssessmentsPage.vue')
const AssessmentWizardPage = () => import('@/assessments/AssessmentWizardPage.vue')
const BrowsePresentLevelsPage = () => import('@/browse/presentlevels/BrowsePresentLevelsPage.vue')
const BrowseGoalsPage = () => import('@/browse/goals/BrowseGoalsPage.vue')
const GoalWizard = () => import('@/goals/GoalWizard.vue')
const StrategyWizard = () => import('@/strategies/StrategyWizard.vue')
const StrategyDetailPage = () => import('@/strategies/StrategyDetailPage.vue')
const SharedStrategyPage = () => import('@/strategies/SharedStrategyPage.vue')
const AnchorPage = () => import('@/goals/AnchorPage.vue')
const SharedAnchorPage = () => import('@/goals/SharedAnchorPage.vue')
const CollectionPage = () => import('@/curated_collections/CollectionPage.vue')
const SavedStrategyPage = () => import('@/strategies/SavedStrategyPage.vue')
const SavedAnchorPage = () => import('@/goals/SavedAnchorPage.vue')
const AdminAnalytics = () => import('@/views/AdminAnalytics.vue')
const AdminUserList = () => import('@/admin/AdminUserList.vue')
const NewAnchorAccessPoints = () => import('@/admin/access-points/NewAnchorAccessPoints.vue')
const EditAnchorAccessPoints = () => import('@/admin/access-points/EditAnchorAccessPoints.vue')
const LibraryFolder = () => import('@/library/LibraryFolder.vue')
const ViewOnlyLibraryFolder = () => import('@/library/ViewOnlyLibraryFolder.vue')
const RecentlySaved = () => import('@/library/recently_saved/RecentlySaved.vue')
const RecentlyViewed = () => import('@/library/recently_viewed/RecentlyViewed.vue')
const PresentlevelsContainer = () => import('@/presentlevels/PresentlevelsContainer.vue')
const PresentlevelsSummaries = () => import('@/presentlevels/PresentlevelsSummaries.vue')
const PresentlevelsSessionWrapper = () => import('@/presentlevels/PresentlevelsSessionWrapper.vue')
const PresentlevelsNewSessionForm = () => import('@/presentlevels/PresentlevelsNewSessionForm.vue')
const PresentlevelsSavedSummaryWrapper = () => import('@/presentlevels/PresentlevelsSavedSummaryWrapper.vue')
const PresentlevelsSavedSummary = () => import('@/presentlevels/PresentlevelsSavedSummary.vue')
const PresentLevelsShell = () => import('@/presentlevels/no-ai/PresentLevelsShell.vue')
const PresentLevelsConclusion = () => import('@/presentlevels/no-ai/PresentLevelsConclusion.vue')
const PresentLevelsConclusionStatement = () => import('@/presentlevels/no-ai/PresentLevelsConclusionStatement.vue')
const PresentLevelsConclusionAssessments = () => import('@/presentlevels/no-ai/PresentLevelsConclusionAssessments.vue')
const PresentLevelsGoalRecommendations = () => import('@/presentlevels/no-ai/PresentLevelsGoalRecommendations.vue')
const PresentLevelsResults = () => import('@/presentlevels/no-ai/PresentLevelsResults.vue')
const PresentLevelsWizardFilters = () => import('@/presentlevels/no-ai/PresentLevelsWizardFilters.vue')
const ImpactOfDisability = () => import('@/impact-of-disability/ImpactOfDisability.vue')
const ImpactOfDisabilitySavedStatementWrapper = () => import('@/impact-of-disability/ImpactOfDisabilitySavedStatementWrapper.vue')
const ImpactOfDisabilitySavedStatement = () => import('@/impact-of-disability/ImpactOfDisabilitySavedStatement.vue')
const SharedImpactOfDisabilityStatement = () => import('@/impact-of-disability/SharedImpactOfDisabilityStatement.vue')
const SharedPresentLevelsPage = () => import('@/presentlevels/no-ai/SharedPresentLevelsPage.vue')
const {LIBRARY_MODE_ALL, LIBRARY_MODE_ARCHIVED, LIBRARY_MODE_OWNED, LIBRARY_MODE_SHARED} = constants

// moving between the wizard and its results is one browsing flow, so the scroll
// position survives the round trip instead of snapping to the top of each page;
// so does each drill-down step on Browse Present Levels
const ROUTES_TO_PRESERVE_SCROLLING = ['StrategyWizard', 'BrowseStrategiesPage', 'BrowseGoalsWizardModal', 'BrowseGoalsPage', 'BrowsePresentLevelsPage']

// A query key given twice arrives as an array and one given without a value as null.
function firstQueryValue(value: LocationQueryValue | LocationQueryValue[] | undefined): string {
  const first = Array.isArray(value) ? value[0] : value
  return first ?? ''
}

function staysWithinOneFlow(toName: unknown, fromName: unknown) {
  return [toName, fromName].every((name) => ROUTES_TO_PRESERVE_SCROLLING.includes(String(name)))
}

const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_ROUTER_BASE),
  scrollBehavior(to, from, savedPosition) {
    // If there's a saved position (browser back/forward), use it
    if (savedPosition) {
      return savedPosition
    }
    if (staysWithinOneFlow(to.name, from.name)) {
      return false
    }
    // Otherwise, scroll to top
    return { top: 0, left: 0 }
  },
  routes: [
    {
      path: '/',
      name: 'Home',
      component: HomePage,
      meta: {
        title: 'Home',
        requiresAuth: true,
        noIndex: true
      }
    },
    {
      path: '/toolkit-vue3-spa',
      name: 'home',
      component: HomeView,
      meta: {
        title: 'Engineer Landing',
      }
    },
    {
      path: '/components',
      name: 'ComponentLib',
      component: ComponentLib,
      meta: {
        title: 'Component Library',
        requiresAuth: true,
        adminAccess: true
      }
    },
    {
      path: '/goal-index',
      name: 'GoalIndexPage',
      component: GoalIndexPage,
      meta: {
        title: 'Learning Goal Index',
        feedbackType: 'Goal Index',
        requiresAuth: true,
        noIndex: true
      }
    },
    {
      path: '/strategies/wizard',
      name: 'StrategyWizard',
      component: StrategyWizard,
      meta: {
        title: 'UDL Strategy Wizard',
        feedbackType: 'Browse Strategies Wizard',
        requiresAuth: true,
        noIndex: true
      }
    },
    {
      path: '/strategies',
      name: 'StrategyIndex',
      component: StrategyIndexPage,
      meta: {
        title: 'UDL Strategies',
        feedbackType: 'UDL Strategy Index',
        requiresAuth: true,
        noIndex: true
      }
    },
    {
      path: '/wizard',
      name: 'BrowseGoalsWizardModal',
      component: GoalWizard,
      meta: {
        title: 'Personalized Learning Goal Wizard',
        feedbackType: 'Browse Goals Wizard',
        requiresAuth: true,
        noIndex: true
      }
    },
    {
      path: '/browse',
      name: 'BrowseGoalsPage',
      component: BrowseGoalsPage,
      meta: {
        title: 'Browse Goals',
        feedbackType: 'Goal Browse',
        requiresAuth: true,
        noIndex: true
      }
    },
    {
      path: '/browse/strategies',
      name: 'BrowseStrategiesPage',
      component: BrowseStrategiesPage,
      meta: {
        title: 'Browse UDL Strategies',
        feedbackType: 'Browse Strategies Results',
        requiresAuth: true,
        noIndex: true
      }
    },
    {
      path: '/browse/assessments',
      name: 'BrowseAssessments',
      component: BrowseAssessmentsPage,
      meta: {
        title: 'Browse Assessments',
        feedbackType: 'Browse Assessments',
        requiresAuth: true,
        noIndex: true
      }
    },
    {
      path: '/wizard/assessments',
      name: 'AssessmentWizard',
      component: AssessmentWizardPage,
      meta: {
        title: 'Assessment Wizard',
        feedbackType: 'Assessment Wizard',
        requiresAuth: true,
        noIndex: true
      }
    },
    {
      path: '/browse/presentlevels',
      name: 'BrowsePresentLevelsPage',
      component: BrowsePresentLevelsPage,
      meta: {
        title: 'Browse Present Levels',
        feedbackType: 'Browse Present Levels',
        requiresAuth: true,
        noIndex: true
      }
    },
    {
      path: '/strategy/:slugOrUuid',
      name: 'StrategyDetailPage',
      component: StrategyDetailPage,
      props: (route) => ({
        slugOrUuid: route.params.slugOrUuid,
        libraryGoalUuid: route.query.lg
      }),
      meta: {
        title: 'UDL Strategies',
        feedbackType: 'Strategy Detail Page',
        requiresAuth: true,
        noIndex: true
      }
    },
    {
      path: '/collections/:id',
      name: 'CuratedCollection',
      component: CollectionPage,
      props: true,
      meta: {
        title: 'Curated Collection',
        feedbackType: 'Curated Collection',
        requiresAuth: true,
        noIndex: true
      }
    },
    {
      path: '/library/strategy/:uuid',
      name: 'SavedStrategyPage',
      component: SavedStrategyPage,
      props: true,
      meta: {
        title: 'UDL Strategies',
        feedbackType: 'Strategy Detail Page',
        requiresAuth: true,
        requiresReloginCheck: true,
        noIndex: true
      }
    },
    {
      path: '/library/anchor-page/:uuid',
      name: 'SavedAnchorPage',
      component: SavedAnchorPage,
      props: true,
      meta: {
        title: 'Saved Anchor Page',
        feedbackType: 'Saved Anchor Page',
        requiresAuth: true,
        requiresReloginCheck: true,
        noIndex: true
      }
    },
    {
      path: '/admin/analytics',
      name: 'AdminAnalytics',
      component: AdminAnalytics,
      meta: {
        title: 'Analytics Dashboard',
        requiresAuth: true,
        requiresInternalOrSchoolAdmin: true
      }
    },
    {
      path: '/admin/anchor-page/new',
      name: 'NewAnchorAccessPoints',
      component: NewAnchorAccessPoints,
      meta: {
        title: 'New Anchor Page w/ Access Points',
        noIndex: true,
        adminAccess: true
      }
    },
    {
      path: '/admin/edit/anchor-page/:uuid',
      name: 'EditAnchorAccessPoints',
      component: EditAnchorAccessPoints,
      props: true,
      meta: {
        title: 'Edit Anchor Page',
        noIndex: true,
        adminAccess: true
      }
    },
    {
      path: '/admin/user_list/:schoolUuid?',
      name: 'AdminUserList',
      component: AdminUserList,
      meta: {
        title: 'Admin User List',
        requiresAuth: true,
        requiresInternalOrSchoolAdmin: true,
        requiresReloginCheck: true,
      }
    },
    {
      path: '/share/folder/:shareToken',
      name: 'ViewOnlyLibraryFolder',
      component: ViewOnlyLibraryFolder,
      props: true,
      meta: {
        title: 'Shared Folder',
        requiresAuth: false
      }
    },
    {
      path: '/share/iod/:uuid',
      name: 'SharedImpactOfDisabilityStatement',
      component: SharedImpactOfDisabilityStatement,
      props: true,
      meta: {
        title: 'Impact of Disability Statement',
        requiresAuth: false
      }
    },
    {
      path: '/anchor-page/:uuid',
      name: 'AnchorPage',
      component: AnchorPage,
      props: (route) => ({
        uuid: route.params.uuid,
        breadcrumbSubject: firstQueryValue(route.query.browse_subject),
        breadcrumbDomain: firstQueryValue(route.query.browse_domain),
        isDraft: route.query.draft === 'true'
      }),
      meta: {
        title: 'Anchor Page',
        feedbackType: 'Anchor Page',
        requiresAuth: true,
        noIndex: true
      }
    },
    {
      path: '/share/anchor-page/:uuid',
      name: 'SharedAnchorPage',
      component: SharedAnchorPage,
      props: true,
      meta: {
        title: 'Shared Anchor Page',
        feedbackType: 'Shared Anchor Page',
        requiresAuth: false,
        noIndex: true
      }
    },
    {
      path: '/library/anchor-page/share/:uuid',
      name: 'SharedAnchorPageDeprecatedUrl',
      redirect: (to) => ({ name: 'SharedAnchorPage', params: { uuid: to.params.uuid } })
    },
    {
      path: '/share/strategy/:uuid',
      name: 'SharedStrategyDetailPage',
      component: SharedStrategyPage,
      props: true,
      meta: {
        title: 'UDL Strategies',
        feedbackType: 'Strategy Detail Page',
        requiresAuth: false,
        noIndex: true
      }
    },
    {
      path: '/share/presentlevels/summary/:uuid',
      name: 'SharedPresentlevelsSummary',
      component: SharedPresentLevelsStatement,
      props: true,
      meta: {
        title: 'Present Levels Statement',
        requiresAuth: false
      }
    },
    {
      path: '/share/presentlevels/:uuid',
      name: 'SharedPresentLevelsPage',
      component: SharedPresentLevelsPage,
      props: true,
      meta: {
        title: 'Shared Present Levels Page',
        requiresAuth: false,
        noIndex: true
      }
    },
    {
      path: '/library/all',
      name: 'AllFolders',
      component: FolderList,
      props: {
        mode: LIBRARY_MODE_ALL
      },
      meta: {
        title: 'All Folders',
        requiresAuth: true,
        requiresReloginCheck: true,
        noIndex: true
      }
    },
    {
      path: '/library',
      name: 'Library',
      redirect: '/library/all'
    },
    {
      path: '/favorites',
      redirect: '/library/all'
    },
    {
      path: '/library/folder/:uuid',
      redirect: (to) => { return { path: `/folder/${to.params.uuid}` } }
    },
    {
      path: '/folder/:uuid',
      name: 'LibraryFolder',
      component: LibraryFolder,
      props: true,
      meta: {
        title: 'Library Folder',
        requiresAuth: true,
        requiresReloginCheck: true,
        noIndex: true
      }
    },
    {
      path: '/library/recently_saved',
      name: 'RecentlySaved',
      component: RecentlySaved,
      meta: {
        title: 'Recently Saved',
        requiresAuth: true,
        requiresReloginCheck: true,
        noIndex: true,
        isPremium: true
      }
    },
    {
      path: '/library/allresources',
      redirect: '/library/recently_saved'
    },
    {
      path: '/library/archived',
      name: 'ArchivedFolders',
      component: FolderList,
      props: {
        mode: LIBRARY_MODE_ARCHIVED
      },
      meta: {
        title: 'Archived Folders',
        requiresAuth: true,
        requiresReloginCheck: true,
        noIndex: true,
        isPremium: true
      }
    },
    {
      path: '/library/recently_viewed/:type',
      name: 'RecentlyViewed',
      props: true,
      component: RecentlyViewed,
      meta: {
        title: 'Recently Viewed',
        requiresAuth: true,
        noIndex: true
      }
    },
    {
      path: '/library/shared',
      name: 'SharedWithMe',
      component: FolderList,
      props: {
        mode: LIBRARY_MODE_SHARED
      },
      meta: {
        title: 'All Folders: Shared with Me',
        requiresAuth: true,
        requiresReloginCheck: true,
        noIndex: true,
        isPremium: true
      }
    },
    {
      path: '/library/owned',
      name: 'MyFolders',
      component: FolderList,
      props: {
        mode: LIBRARY_MODE_OWNED
      },
      meta: {
        title: 'All Folders: My Folders',
        requiresAuth: true,
        requiresReloginCheck: true,
        noIndex: true,
        isPremium: true
      },
    },
    {
      path: '/share/student/:shareToken',
      redirect: (to) => ({ path: `/share/folder/${to.params.shareToken}` })
    },
    {
      path: '/student/:uuid',
      redirect: (to) => ({ path: `/folder/${to.params.uuid}` })
    },
    {
      path: '/presentlevels',
      component: PresentLevelsShell,
      meta: {
        title: 'Present Levels',
        feedbackType: 'Present Levels Wizard',
        requiresAuth: true,
        noIndex: true
      },
      children: [
        {
          path: 'conclusion/:uuid',
          component: PresentLevelsConclusion,
          props: true,
          meta: {
            title: 'Present Levels Conclusion',
            requiresAuth: true,
            noIndex: true
          },
          children: [
            {
              path: 'assessments',
              name: CONCLUSION_ROUTE_NAMES.ASSESSMENTS,
              component: PresentLevelsConclusionAssessments,
              meta: {
                title: 'Present Levels Conclusion - Assessments',
                requiresAuth: true,
                noIndex: true
              }
            },
            {
              path: 'goals-strategies',
              name: CONCLUSION_ROUTE_NAMES.GOALS_STRATEGIES,
              component: PresentLevelsGoalRecommendations,
              meta: {
                title: 'Present Levels Conclusion - Goals & Strategies',
                requiresAuth: true,
                noIndex: true
              }
            },
            {
              path: '',
              name: CONCLUSION_ROUTE_NAMES.STATEMENT,
              component: PresentLevelsConclusionStatement,
              props: true,
              meta: {
                title: 'Present Levels Conclusion',
                requiresAuth: true,
                noIndex: true
              }
            }
          ]
        },
        {
          path: 'results/:uuid/:subject',
          name: 'PresentlevelsSubjectResults',
          component: PresentLevelsResults,
          props: true,
          meta: {
            title: 'Personalized Browse Present Levels',
            requiresAuth: true,
            noIndex: true
          }
        },
        {
          path: 'results/:uuid',
          name: 'PresentlevelsResults',
          component: PresentLevelsResults,
          props: true,
          meta: {
            title: 'Personalized Browse Present Levels',
            requiresAuth: true,
            noIndex: true
          }
        },
        {
          path: 'browse/:subject',
          name: 'PresentlevelsBrowse',
          component: PresentLevelsResults,
          props: true,
          meta: {
            title: 'Browse Present Levels',
            requiresAuth: true,
            noIndex: true
          }
        },
        {
          path: ':uuid',
          name: 'PresentLevelsExistingWizard',
          component: PresentLevelsWizardFilters,
          props: true,
          meta: {
            title: 'Present Levels Wizard',
            requiresAuth: true,
            noIndex: true
          }
        },
        {
          path: '',
          name: 'PresentLevelsNewWizard',
          component: PresentLevelsWizardFilters,
          props: { uuid: null },
          meta: {
            title: 'Present Levels Wizard',
            requiresAuth: true,
            noIndex: true
          }
        }
      ]
    },
    {
      path: '/presentlevels/v2',
      name: 'Presentlevels',
      component: PresentlevelsContainer,
      meta: {
        title: 'Present Levels Wizard',
        requiresAuth: true,
      },
      children: [
        {
          path: '',
          name: 'PresentlevelsSessionWrapper',
          component: PresentlevelsSessionWrapper,
          meta: {
            title: 'Present Levels Wizard',
            requiresAuth: true,
          },
          children: [
            {
              path: 'session',
              name: 'PresentlevelsSession',
              meta: {
                title: 'Present Levels Wizard',
                requiresAuth: true,
              },
              children: [
                {
                  path: '',
                  name: 'PresentlevelsNewSession',
                  component: PresentlevelsNewSessionForm,
                  meta: {
                    title: 'Present Levels Wizard',
                    requiresAuth: true,
                  }
                },
                {
                  path: ':session_uuid',
                  name: 'PresentlevelsCurrentSession',
                  component: PresentlevelsSummaries,
                  props: true,
                  meta: {
                    title: 'Present Levels Wizard',
                    requiresAuth: true,
                  }
                }
              ]
            }
          ]
        },
        {
          path: 'summary',
          name: 'PresentlevelsSavedSummaryWrapper',
          component: PresentlevelsSavedSummaryWrapper,
          meta: {
            title: 'Present Levels Wizard',
            requiresAuth: true,
          },
          children: [
            {
              path: ':summary_uuid',
              name: 'PresentlevelsSavedSummary',
              component: PresentlevelsSavedSummary,
              props: true,
              meta: {
                title: 'Present Levels Wizard',
                requiresAuth: true,
              }
            }
          ]
        }
      ]
    },
    {
      path: '/impact-of-disability',
      name: 'ImpactOfDisability',
      component: ImpactOfDisability,
      meta: {
        title: 'Impact of Disability Statement',
        requiresAuth: true,
        requiresReloginCheck: true
      }
    },
    {
      path: '/impact-of-disability/v2',
      redirect: '/impact-of-disability',
    },
    {
      path: '/library/iod/:statement_uuid',
      name: 'ImpactOfDisabilitySavedStatementWrapper',
      component: ImpactOfDisabilitySavedStatementWrapper,
      meta: {
        title: 'Impact of Disability Statement',
        requiresAuth: true,
      },
      children: [
        {
          path: '',
          name: 'ImpactOfDisabilitySavedStatement',
          component: ImpactOfDisabilitySavedStatement,
          props: true,
          meta: {
            title: 'Impact of Disability Statement',
            requiresAuth: true,
          }
        }
      ]
    },
  ]
})

import { useRouterStore } from './router.store'
import SharedPresentLevelsStatement from '@/presentlevels/SharedPresentLevelsStatement.vue'


router.beforeEach(authRouteGuard)
router.beforeEach(reloginRouteGuard)
router.beforeEach(betaRouteGuard)
router.beforeEach(internalOrSchoolAdminRouteGuard)

router.beforeEach((to, from, next) => {
  const routerStore = useRouterStore()
  if (!to.meta.isPremium) {
    routerStore.markLastNonPremiumPage(to)
  }
  next()
})

router.beforeEach(premiumRouteGuard)

router.afterEach((to) => {
  setTitle(to.meta.title)
})

export default router
